import { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { useWaitlistSignup } from '@/hooks/useWaitlistSignup';
import { getWaitlistCache, setWaitlistCache } from '@/lib/waitlistCache';

interface WaitlistModalProps {
  isOpen: boolean;
  onClose: () => void;
  artist: { id: string; name: string };
}

export function WaitlistModal({ isOpen, onClose, artist }: WaitlistModalProps) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [discord, setDiscord] = useState('');
  const { mutate: signup, isPending, error, isSuccess, reset } = useWaitlistSignup();

  useEffect(() => {
    if (isOpen) {
      const cached = getWaitlistCache();
      if (cached) {
        setName(cached.name);
        setEmail(cached.email);
        setDiscord(cached.discord || '');
      }
      reset();
    }
  }, [isOpen, reset]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    setWaitlistCache({ name, email, discord: discord || undefined });

    signup({
      artistId: artist.id,
      artistName: artist.name,
      clientName: name,
      clientEmail: email,
      clientDiscord: discord || undefined
    });
  };

  const handleClose = () => {
    if (isSuccess) {
      setName('');
      setEmail('');
      setDiscord('');
    }
    onClose();
  };

  if (!isOpen) return null;

  const isDuplicate = (error as Error & { code?: string })?.code === 'duplicate';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
      onClick={handleClose}
    >
      <div
        className="bg-[var(--color-bg-card)] rounded-2xl p-6 max-w-md w-full mx-4 shadow-xl animate-in fade-in zoom-in duration-200"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-[var(--color-text)]">
            Join {artist.name}'s Waitlist
          </h2>
          <button
            onClick={handleClose}
            className="text-[var(--color-text-muted)] hover:text-[var(--color-text)] transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {isSuccess ? (
          <div className="text-center py-8">
            <div className="text-4xl mb-4">🎉</div>
            <p className="text-[var(--color-primary)] text-lg font-medium mb-2">
              You're on the list!
            </p>
            <p className="text-[var(--color-text-muted)]">
              We'll contact you when {artist.name} opens for commissions.
            </p>
            <button
              onClick={handleClose}
              className="mt-6 px-6 py-2 rounded-full bg-[var(--color-bg-hover)] text-[var(--color-text)] font-medium hover:bg-[var(--color-border)] transition-colors"
            >
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Honeypot — hidden from humans, bots fill it and get rejected server-side */}
            <input
              type="text"
              name="website"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              style={{ position: "absolute", left: "-9999px", width: 0, height: 0, opacity: 0 }}
              defaultValue=""
            />
            <p className="text-sm text-[var(--color-text-muted)] mb-4">
              Get notified when this artist opens for commissions.
            </p>

            <div>
              <label className="block text-sm font-medium text-[var(--color-text)] mb-1">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                placeholder="Your name"
                className="w-full px-4 py-2 rounded-lg bg-[var(--color-bg)] border border-[var(--color-border)] text-[var(--color-text)] placeholder:text-[var(--color-text-muted)] focus:border-[var(--color-primary)] focus:outline-none transition-colors"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[var(--color-text)] mb-1">
                Email <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="your@email.com"
                className="w-full px-4 py-2 rounded-lg bg-[var(--color-bg)] border border-[var(--color-border)] text-[var(--color-text)] placeholder:text-[var(--color-text-muted)] focus:border-[var(--color-primary)] focus:outline-none transition-colors"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[var(--color-text)] mb-1">
                Discord <span className="text-[var(--color-text-muted)] font-normal">(optional)</span>
              </label>
              <input
                type="text"
                value={discord}
                onChange={(e) => setDiscord(e.target.value)}
                placeholder="username"
                className="w-full px-4 py-2 rounded-lg bg-[var(--color-bg)] border border-[var(--color-border)] text-[var(--color-text)] placeholder:text-[var(--color-text-muted)] focus:border-[var(--color-primary)] focus:outline-none transition-colors"
              />
            </div>

            {error && (
              <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20">
                <p className="text-red-500 text-sm">
                  {isDuplicate
                    ? "You're already on this artist's waitlist!"
                    : 'Something went wrong. Please try again.'}
                </p>
              </div>
            )}

            <button
              type="submit"
              disabled={isPending}
              className="w-full py-3 rounded-full bg-[var(--color-primary)] text-[var(--color-bg)] font-semibold hover:bg-[var(--color-primary-light)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isPending ? 'Joining...' : 'Join Waitlist'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
